import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import { ServerRepresentedEvent } from '../../../../events/models/event.model'
import { PersonaTypes } from '../../../../personas/enums/persona-types.enum'

@Component({
  selector: 'ngx-admin-event-card',
  templateUrl: './admin-event-card.component.html',
  styleUrls: ['./admin-event-card.component.scss'],
  imports: [CommonModule]
})
export class AdminEventCardComponent {
  @Input() event!: ServerRepresentedEvent
  @Input() isSelected: boolean = false
  @Input() context: 'badge-editor' | 'group-forms' | 'event-tools' | 'default' = 'default'
  @Output() cardClick = new EventEmitter<ServerRepresentedEvent>()
  @Output() editGroupForm = new EventEmitter<ServerRepresentedEvent>()
  @Output() editSpecificRelationship = new EventEmitter<{ event: ServerRepresentedEvent; relationship: any }>()
  @Output() createNewRelationship = new EventEmitter<ServerRepresentedEvent>()
  @Output() openBadge = new EventEmitter<ServerRepresentedEvent>()
  @Output() openGroupForms = new EventEmitter<ServerRepresentedEvent>()
  @Output() openSettings = new EventEmitter<ServerRepresentedEvent>()

  /**
   * Gets the current status of an event based on its dates
   */
  public getEventStatus(event: ServerRepresentedEvent): string {
    if (!event || !event.public_start_date || !event.public_end_date) {
      return 'Unknown'
    }

    try {
      const now = new Date()
      const startDate = new Date(event.public_start_date)
      const endDate = new Date(event.public_end_date)

      // Check if dates are valid
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return 'Unknown'
      }

      // Fix the logic: check if event has ended, is currently running, or hasn't started yet
      if (now > endDate) {
        return 'Finished' // Event has ended
      } else if (now >= startDate && now <= endDate) {
        return 'Live' // Event is currently running
      } else if (now < startDate) {
        return 'Upcoming' // Event hasn't started yet
      } else {
        return 'Unknown'
      }
    } catch (error) {
      console.warn('Error calculating event status:', error)
      return 'Unknown'
    }
  }

  /**
   * Gets the status display info for an event based on date calculation
   */
  public getStatusDisplay(event: ServerRepresentedEvent): { text: string; class: string } {
    if (!event) {
      return { text: 'Unknown', class: 'status-unknown' }
    }

    const status = this.getEventStatus(event)

    switch (status) {
      case 'Finished':
        return { text: 'Finished', class: 'status-completed' }
      case 'Live':
        return { text: 'Live', class: 'status-live' }
      case 'Upcoming':
        return { text: 'Upcoming', class: 'status-published' }
      default:
        return { text: 'Unknown', class: 'status-unknown' }
    }
  }

  /**
   * Gets the number of forms for an event
   */
  public getEventFormsCount(event: ServerRepresentedEvent): number {
    if (!event || !event.forms) {
      return 0
    }
    return event.forms.length || 0
  }

  /**
   * Gets the number of group form relationships for an event
   */
  public getGroupFormRelationshipsCount(event: ServerRepresentedEvent): number {
    if (!event || !event.forms) {
      return 0
    }
    // Count forms with type GROUP (30) - these are parent forms in group relationships
    const groupForms = event.forms.filter((form) => form.type === PersonaTypes.GROUP)
    return groupForms.length
  }

  /**
   * Gets the number of valid VISITOR forms for creating new group relationships
   */
  public getValidFormsForGroupCreation(event: ServerRepresentedEvent): number {
    if (!event || !event.forms) {
      return 0
    }
    // Count forms with type VISITOR (10) that can be used for group relationships
    const validForms = event.forms.filter((form) => form.type === PersonaTypes.VISITOR)
    return validForms.length
  }

  /**
   * Checks if event can create new group form relationships
   */
  public canCreateGroupRelationships(event: ServerRepresentedEvent): boolean {
    return this.getValidFormsForGroupCreation(event) >= 2
  }

  /**
   * Checks if event has existing group form relationships
   */
  public hasExistingGroupRelationships(event: ServerRepresentedEvent): boolean {
    return this.getGroupFormRelationshipsCount(event) > 0
  }

  public getGroupFormRelationships(event: ServerRepresentedEvent): any[] {
    if (!event || !event.forms) {
      return []
    }
    // Get forms with type GROUP (30) - these are parent forms in group relationships
    const groupForms = event.forms.filter((form) => form.type === PersonaTypes.GROUP)
    return groupForms.map((parentForm) => {
      // Find the corresponding child form
      const childForm = event.forms?.find((form) => form.parent === parentForm.id)
      return {
        id: parentForm.id,
        parentForm: parentForm,
        childForm: childForm,
        relationship: `${parentForm.name} → ${childForm?.name || 'Unknown'}`,
        parentSlug: parentForm.slug,
        childSlug: childForm?.slug
      }
    })
  }

  /**
   * Handles card click
   */
  public onCardClick(): void {
    if (this.context === 'event-tools') {
      return
    }
    if (this.event) {
      this.cardClick.emit(this.event)
    }
  }

  public onOpenBadgeClick(event: Event): void {
    event.stopPropagation()
    if (this.event) {
      this.openBadge.emit(this.event)
    }
  }

  public onOpenGroupFormsClick(event: Event): void {
    event.stopPropagation()
    if (this.event) {
      this.openGroupForms.emit(this.event)
    }
  }

  public onOpenSettingsClick(event: Event): void {
    event.stopPropagation()
    if (this.event) {
      this.openSettings.emit(this.event)
    }
  }

  /**
   * Handles edit group form click
   */
  public onEditGroupFormClick(event: Event): void {
    event.stopPropagation() // Prevent card click
    if (this.event) {
      this.editGroupForm.emit(this.event)
    }
  }

  public onEditSpecificRelationshipClick(event: Event, relationship: any): void {
    event.stopPropagation() // Prevent card click
    if (this.event) {
      this.editSpecificRelationship.emit({ event: this.event, relationship: relationship })
    }
  }

  public onCreateNewRelationshipClick(event: Event): void {
    event.stopPropagation() // Prevent card click
    if (this.event) {
      this.createNewRelationship.emit(this.event)
    }
  }

  /**
   * Tracks a relationship by its ID
   * @param index {number} - The index of the relationship
   * @param relationship {any} - The relationship to track
   * @returns {number} - The ID of the relationship
   * */
  public trackByRelationshipId(index: number, relationship: any): any {
    return relationship?.id || index
  }
}
